import React, { useEffect, useState } from 'react';
import { BanknotesIcon, CalendarIcon, ChevronLeftIcon, CreditCardIcon } from '@heroicons/react/16/solid';
import { Link, useNavigate, useParams } from 'react-router-dom'; // useParams to get route params
import { Heading, Subheading } from '../../../../components/Heading';
import { Badge } from '../../../../components/Badge';
import { Button } from '../../../../components/Button';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../../../../components/Description-List';
import { Card } from '../../../../components/Card';
import { Avatar } from '../../../../components/Avatar';
import { UserService } from '../../../../services/userService'; // Import the UserService
import Loading from '../../../../components/Loading';
import dateFormat from 'dateformat';
import { useSetupUsergroup } from '../../../../hooks/useSetupUsergroup';
import { SetupUsergroup } from '../../../../models/SetupUsergroup';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';

const SetupViewCustomerIdPage: React.FC = () => {
    const { id } = useParams<{ id: string }>(); // Use useParams to get the id from the URL
    const navigate = useNavigate();
    const [customer, setCustomer] = useState<SetupUsergroup | null>(null);
    const { fetchUserGroupById, loading, error } = useSetupUsergroup();

    useEffect(() => {
        document.title = "View Customer";
    }, []);

    useEffect(() => {
        const fetchCustomerDetails = async () => {
            if (!id) return; // Ensure ID is valid before making the request

            try {
                const fetchedcustomer = await fetchUserGroupById(Number(id));
                setCustomer(fetchedcustomer);
            } catch (err) {
                showErrorToast('Failed to fetch customer details.');
            }
        };
        fetchCustomerDetails();
    }, [id]);

    if (loading) return <Loading />;
    if (error) return <p className='py-16 text-center text-red-700'>{error}</p>;
    if (!customer) return <p className='py-16 text-center'>Customer not found.</p>;

    return (
        <>
            <div className="max-lg:hidden">
                <Link to="/gp/setup/customers" className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 dark:text-zinc-400">
                    <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                    Customers
                </Link>
            </div>
            <div className="mt-4 lg:mt-8">
                <div className="flex items-center gap-4">
                    <Avatar square initials={customer.fullname[0]} className="bg-zinc-300 text-gray-700 size-8" />
                    <Heading>{customer.fullname}</Heading>
                    <Badge color="lime">{customer.usergroup_name!}</Badge>
                </div>
                <div className="isolate mt-2.5 flex flex-wrap justify-between gap-x-6 gap-y-4">
                    <div className="flex flex-wrap gap-x-10 gap-y-4 py-1.5">
                        <span className="flex items-center gap-2 text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white">
                            <CalendarIcon className="size-4 shrink-0 fill-zinc-400 dark:fill-zinc-500" />
                            <span>{dateFormat(customer.created_time, "mmmm dd, yyyy")}</span>
                        </span>
                    </div>
                    <div className="flex gap-4">
                        <Button onClick={() => navigate(`/gp/setup/customers/edit/${customer.id}`)}>Edit</Button>
                    </div>
                </div>
            </div>
            <Card className="mt-12 p-6">
                <Subheading>customer Information</Subheading>
                <DescriptionList className="mt-4">
                    <DescriptionTerm>Fullname</DescriptionTerm>
                    <DescriptionDetails>{customer.fullname}</DescriptionDetails>
                    <DescriptionTerm>Second Name</DescriptionTerm>
                    <DescriptionDetails>{customer.second_name}</DescriptionDetails>
                    <DescriptionTerm>Area</DescriptionTerm>
                    <DescriptionDetails>{customer.area}</DescriptionDetails>
                    <DescriptionTerm>Group</DescriptionTerm>
                    <DescriptionDetails>{customer.group}</DescriptionDetails>
                    <DescriptionTerm>Office No</DescriptionTerm>
                    <DescriptionDetails>{customer.office_no}</DescriptionDetails>
                    <DescriptionTerm>Fax No</DescriptionTerm>
                    <DescriptionDetails>{customer.fax_no}</DescriptionDetails>
                    <DescriptionTerm>Mobile No</DescriptionTerm>
                    <DescriptionDetails>{customer.mobile_no}</DescriptionDetails>
                    <DescriptionTerm>Account Code</DescriptionTerm>
                    <DescriptionDetails>{customer.account_code}</DescriptionDetails>
                    <DescriptionTerm>Address</DescriptionTerm>
                    <DescriptionDetails>{customer.address}</DescriptionDetails>
                    <DescriptionTerm>Address 2</DescriptionTerm>
                    <DescriptionDetails>{customer.address_2}</DescriptionDetails>
                    <DescriptionTerm>Address 3</DescriptionTerm>
                    <DescriptionDetails>{customer.address_3}</DescriptionDetails>
                    <DescriptionTerm>Address 4</DescriptionTerm>
                    <DescriptionDetails>{customer.address_4}</DescriptionDetails>
                    <DescriptionTerm>Email</DescriptionTerm>
                    <DescriptionDetails>{customer.email || '-'}</DescriptionDetails>
                    <DescriptionTerm>Warehouse</DescriptionTerm>
                    <DescriptionDetails>{customer.warehouse_name || '-'}</DescriptionDetails>
                </DescriptionList>
            </Card>
        </>
    );
};

export default SetupViewCustomerIdPage;
